import RemixImageManager from "./RemixImageManager.js";
import SharePostcardMaker from "./SharePostcardMaker.js";
import Ripple from "./Ripple.js";
import Scrollable from "./Scrollable.js";
import AxL from "./axl.js";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const AbortStates = {
  DEFAULT: 0,
  REMIX: 1,
  FETCH: 2,
  CANCEL: 3,
};

const MessageStates = {
  SURGE_MODE: 1000,
};

const API_REQUEST_TIMEOUT_MS = 20000;

const API_REMIX_TIMEOUT_MS = 60000;

const Endpoints = {
  GET_RANDOM_ASSETS: 'getRandomAssets',
  GET_ASSET_DATA: 'getAssetData',
  CHECK_TEXT_INPUT: 'checkTextInput',
  GET_REMIX_IMAGE: 'getRemixImage',
};

const defaultAsset = {
  assetId: 'hwEGmsM-FoHAwA',
  aiRemixPromptGroups: [
    ["sunflowers", "baked goods", "icy sunflowers", "withered flowers"],
    ["a vase", "a Chinese vase", "a pineapple", "a broken vase"],
    ["a yellow wall", "a sunflower field", "the van Gogh's bedroom", "an igloo wall"],
  ],
  aiRemixPromptTemplate: 'An oil painting of {} arranged in {} against {}',
  creators: ['Vincent Van Gogh'],
  imageUrl: 'https://lh3.googleusercontent.com/ci/AA1T9HIRw5R_46K3z8Sov8MJqu--uJbrqNpnCM6FBDmKsWm2uUTeDLAl1PzSldvWuIbaCgXvGvCIq_4',
  partnerName: 'Van Gogh Museum',
  title: 'Sunflowers',
  contentType: 'PAINTING',
  artMovements: ['Modern art', 'Post-Impressionism'],
  mediums: ['Oil paint', 'Canvas'],
};

/**
 * Whether user is in Culture Studio.
 */
const isCultureStudio = navigator.userAgent.toLowerCase().includes('culturestudio');
const isiOSWebview = navigator.userAgent.toLowerCase().includes('ioswebview');

export default class ArtRemix {
  constructor() { }

  //------ initializers
  async init(data) {
    this.axl = window.axl;

    // defaults and initilizations

    // By default the noise strenght is set to 80% and not 100%.
    this.noiseStrength = 0.8;
    this.remixImage = new RemixImageManager();
    this.sharePostcardMaker = new SharePostcardMaker();

    this.assetsMap = new Map();
    this.assetsDataInc = -1;
    this.randomAssets = [];
    this.currentRemixUuid = null;

    this.history = [];

    this.modelTimeoutTimer = null;

    this.loadRemixAbortController = null;
    this.abortReason = null;

    this.defaultFetchAbortController = null;
    this.defaultFetchTimeoutTimer = null;

    // A global field to allow sending requests for remixing.
    // This avoids spamming the endpoint.
    this.canSendArtRemixRequest = true;

    // init ui events
    this.initUiEventListeners();
    this.initScrollable();

    // Intro leaving
    setTimeout(() => {
      document.getElementById("intro").classList.add("hide");
      document
        .getElementById("painting-container")
        .classList.remove("hide-before-intro");
      document
        .getElementById("description-inner")
        .classList.remove("hide-before-intro");
      if (this.history.length === 0) {
        this.showPage("main-image");
      }
    }, 1000);

    if (!!window['_surgeMode']) {
      const promptContainer = document.getElementById("prompt-error-container");
      promptContainer.classList.remove('hide');
      promptContainer.setAttribute('data-state', MessageStates.SURGE_MODE);
    }

    this.axl.sendMessage(AxL.ChildToHost.TRACK, {
      action: 'experiment load',
      category: 'websandbox_2gFc5DkygEURfw',
      label: data.assetId || 'unset',
    });

    this.axl.registerMessageCallback(AxL.HostToChild.STUDIO_ACTION, (payload) => {
      const command = payload['c'];
      if (command == 'sa') {
        const newAssetId = payload['a'];
        if (newAssetId != null) {
          this.onSwitchArtworkFromHost(newAssetId);
        }
      }
    });

    // Detect hash to go page directly
    const hash = document.location.hash.substr(1);
    if (hash) {
      this.history.push("main-image");
      this.showPage(hash);
    }

    this.responsiveState = 'portrait';
    window.addEventListener('resize', this.onResize.bind(this));
    this.onResize();

    await this.setAssets(data.assetId);

    if (!AxL.can["download"]) {
      document.body.classList.add('no-download');
    }

    if (isCultureStudio) {
      // Lets stylesheets adapt to the Culture Studio host (e.g. its header).
      document.body.classList.add('culture-studio');
      document.getElementById("remix-nav").style.display = "none";
      document.getElementById("your-creations-button").style.display = "none";
      document.getElementById("regen-button").style.display = "none";
      if (isiOSWebview) {
        // Change the share icon to iOS share icon.
        let shareButton = document.getElementById("mobile-main-image-share-button-share");
        let iconElement = shareButton.querySelector('.material-icons');
        iconElement.innerText = 'ios_share';
      }
    } else {
      document.getElementById("mobile-remix-nav").style.display = "none";
    }
  }

  async setAssets(assetToShow) {
    const assets = await this.getRandomAssets();

    for (const asset of assets) {
      this.assetsMap.set(asset.assetId, asset);
    }

    if (assetToShow == null) {
      for (let [key, item] of this.assetsMap) {
        if (item.assetId && assetToShow !== null) {
          assetToShow = item.assetId;
        }
      }
    } else {
      const firstAsset = await this.getAssetData(assetToShow);
      this.assetsMap.set(assetToShow, firstAsset);
    }

    this.initMainImage(assetToShow);
    this.setMoreArtworks();
  }

  chooseRandomAsset(assetsData) {
    let rand = Math.floor(Math.random() * assetsData.length - 1);
    return assetsData[rand].assetId;
  }

  hasUrlParam(param) {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    if (urlParams.has(param)) return true;
    return false;
  }

  getUrlParam(param) {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    if (urlParams.has(param)) return urlParams.get(param);
    return null;
  }

  initUiEventListeners() {
    const buttons = document.querySelectorAll('.button-with-color');
    for (const button of buttons) {
      button.addEventListener('click', this.onButtonClick.bind(this));
    }

    document.getElementById("randommix-button").addEventListener(
      "click",
      function () {
        this.randomMix();
      }.bind(this)
    );

    document.getElementById("mobile-randommix-button").addEventListener(
      "click",
      function () {
        this.randomMix();
      }.bind(this)
    );

    document.getElementById("reset-all-button").addEventListener(
      "click",
      function () {
        this.doResetAll();
      }.bind(this)
    );

    document.getElementById("regen-button").addEventListener(
      "click",
      function () {
        this.remix();
      }.bind(this)
    );

    document.getElementById("mobile-main-image-regen-button").addEventListener(
      "click",
      function () {
        this.remix();
      }.bind(this)
    );

    document.getElementById('cancel-button').addEventListener('click', () => {
      this.cancelRemix();
    });

    document.getElementById("main-image-share-button-share").addEventListener(
      "click",
      function () {
        this.share();
      }.bind(this)
    );

    document.getElementById("mobile-main-image-share-button-share").addEventListener(
      "click",
      function () {
        this.share();
      }.bind(this)
    );

    document.getElementById("main-image-download-button").addEventListener(
      "click",
      function (e) {
        this.download(e);
      }.bind(this)
    );

    document.getElementById("mobile-main-image-download-button").addEventListener(
      "click",
      function (e) {
        this.saveCreation(e);
      }.bind(this)
    );

    document.getElementById("enter-text").addEventListener(
      "keydown",
      function (event) {
        if (event.key == "Enter") {
          event.preventDefault();
          this.checkTextInput(event.currentTarget.value);
        }
      }.bind(this)
    );

    document.getElementById("enter-text-button").addEventListener(
      "click",
      function (event) {
        let text = document.getElementById("enter-text").value;
        this.checkTextInput(text);

        this.axl.sendMessage(AxL.ChildToHost.TRACK, {
          action: 'refined result',
          category: 'websandbox_2gFc5DkygEURfw',
          label: `manual option - ${this.current_asset_data.assetId}`,
        });
      }.bind(this)
    );

    document.getElementById("noise-level-input").addEventListener(
      "input", (event) => {
        this.noiseStrength = event.target.value / 100.0;
        document.getElementById("noise-level-percentage").textContent = `${event.target.value}%`
        const noiseLevelInput = document.getElementById("noise-level-input");
        noiseLevelInput.style.background = `linear-gradient(to right, var(--color-blue-medium) 0%, var(--color-blue-medium) ${event.target.value}%, var(--color-grey-500) ${event.target.value}%, var(--color-grey-500) 100%)`
      }
    )

    document.getElementById('more-artworks-button-refresh').addEventListener('click', (e) => {
      e.currentTarget.classList.add('loading');
      this.setAssets();
    });

    // ---o Pages
    const buttonPageList = document.querySelectorAll('[data-class="button-page"]');
    buttonPageList.forEach((buttonPage) => {
      buttonPage.addEventListener("click", (e) => {
        const targetPage = e.currentTarget.getAttribute("data-target");
        if (!targetPage) {
          return;
        }

        if (isCultureStudio) {
          // Instead of switching to another page, call host when opened from Studio.
          let studioCommand = null;
          switch (targetPage) {
            case 'more-artworks':
              studioCommand = 'ma';
              break;
            default:
              break
          }
          if (studioCommand) {
            this.sendStudioAction(studioCommand);
          }
          return;
        }

        this.showPage(targetPage);

        let action = null;

        switch (targetPage) {
          case 'more-artworks':
            action = 'click more artwork';
            break;
          case 'your-creations':
            action = 'click your creations';
          default:
            break
        }

        if (action) {
          this.axl.sendMessage(AxL.ChildToHost.TRACK, {
            action: action,
            category: 'websandbox_2gFc5DkygEURfw',
          });
        }
      });
    });

    const buttonBackPageList = document.querySelectorAll('[data-class="button-back-page"]');
    buttonBackPageList.forEach((buttonBackPage) => {
      buttonBackPage.addEventListener("click", (e) => {
        const targetPage = this.history[this.history.length - 2];
        this.showPage(targetPage, true /* isBack */);
      });
    });

    document
      .querySelector("#button-prompt-back")
      .addEventListener("click", (e) => {
        this.setMainImage(this.current_asset_data.assetId);
        this.setPageMainImageState("original");
      });

    // ---o Popins
    const buttonPopinList = document.querySelectorAll('[data-class="button-popin"]');
    buttonPopinList.forEach((buttonPopin) => {
      buttonPopin.addEventListener("click", (e) => {
        const targetPopin = e.currentTarget.getAttribute("data-target");
        document.querySelector(`.popin#${targetPopin}`).classList.add("active");

        let action = null;

        switch (targetPopin) {
          case 'more-info':
            action = 'click info';
            break;
          default:
            break
        }

        if (action) {
          this.axl.sendMessage(AxL.ChildToHost.TRACK, {
            action: action,
            category: 'websandbox_2gFc5DkygEURfw',
          });
        }
      });
    });

    const buttonPopinCloseList = document.querySelectorAll(
      '[data-class="button-popin-close"]'
    );
    buttonPopinCloseList.forEach((buttonPopinClose) => {
      buttonPopinClose.addEventListener("click", (e) => {
        const targetPopin = e.currentTarget.closest(".popin");
        targetPopin.classList.remove("active");
      });
    });

    const popinList = document.querySelectorAll(".popin");
    popinList.forEach((popin) => {
      popin.addEventListener("click", (e) => {
        if (e.target.className === "popin active") {
          e.target.classList.remove("active");
        }
      });
    });

    document
      .querySelector("#button-prompt-change-back")
      .addEventListener("click", (e) => {
        this.hidePromptView();
      });

    document
      .getElementById("more-artworks-button-remix")
      .addEventListener("click", () => {
        const activeAsset = document.querySelector("#more-artworks .active");

        if (!activeAsset) {
          return;
        }

        const activeAssetId = activeAsset.getAttribute("data-assetid");
        this.setMainImage(activeAssetId);
        this.setPageMainImageState("original");
        this.showPage("main-image", true /* isBack */);

        this.axl.sendMessage(AxL.ChildToHost.TRACK, {
          action: 'click random remix',
          category: 'websandbox_2gFc5DkygEURfw',
          label: `more artworks - ${this.current_asset_data.assetId}`,
        });
      });

    document.getElementById("prompt-error-close").addEventListener(
      "click",
      function () {
        document.getElementById("prompt-error-container").classList.add("hide");
      }.bind(this)
    );

    document.getElementById('your-creations-button-share').addEventListener(
      "click",
      function (event) {
        let creationsEl = document.querySelector(
          "#your-creations li.active div"
        );
        let uuid = creationsEl.getAttribute("data-thumb-uuid");
        this.doShare(uuid, this.current_asset_data.assetId);

        this.axl.sendMessage(AxL.ChildToHost.TRACK, {
          action: 'click share icon',
          category: 'websandbox_2gFc5DkygEURfw',
          label: `your creations - ${this.current_asset_data.assetId}`,
        });
      }.bind(this)
    )

    // ---------o Pagination
    const paginationList = document.querySelectorAll(".pagination");

    for (const pagination of paginationList) {
      const paginationButtons =
        pagination.querySelectorAll(".pagination-button");

      for (const paginationButton of paginationButtons) {
        paginationButton.addEventListener(
          "click",
          this.onPaginationButtonClick.bind(this)
        );
      }
    }

    document.getElementById("your-creations-button-download").addEventListener(
      "click",
      function (event) {
        let selected = document.querySelector(
          "#your-creations .active"
        ).firstChild;
        let uuid = selected.getAttribute("data-thumb-uuid");
        let assetId = selected.getAttribute("data-thumb-assetId");
        this.doDownloadShareCard(uuid, assetId);

        this.axl.sendMessage(AxL.ChildToHost.TRACK, {
          action: 'click download',
          category: 'websandbox_2gFc5DkygEURfw',
        });
      }.bind(this)
    );

    document.getElementById("your-creations-prompt").addEventListener(
      "click",
      function (event) {
        if (event.target.classList.value.indexOf('scrollable') > -1) {
          return;
        }

        this.editYourCreation();
      }.bind(this)
    );
  }

  initScrollable() {
    const $scrollableList = document.querySelectorAll('.scrollable-container');
    this.scrollables = [];
    for (const $scrollable of $scrollableList) {
      this.scrollables.push(new Scrollable($scrollable));
    }
  }

  onButtonClick(e) {
    const button = e.currentTarget;
    const buttonBounds = button.getBoundingClientRect();
    const posX = e.pageX - buttonBounds.left;
    const posY = e.pageY - buttonBounds.top;

    new Ripple(button, posX, posY);
  }

  download(e) {
    e.stopPropagation();
    let assetId = e.currentTarget.getAttribute("data-assetId");
    let uuid = e.currentTarget.getAttribute("data-uuid");
    this.doDownloadShareCard(uuid, assetId);
  }

  saveCreation(e) {
    e.stopPropagation();
    let assetId = e.currentTarget.getAttribute("data-assetId");
    let uuid = e.currentTarget.getAttribute("data-uuid");
    this.doSaveCreation(uuid, assetId);
  }

  share() {
    let creationsEl = document.querySelector(
      "#your-creations li:first-child div"
    );
    let uuid = creationsEl.getAttribute("data-thumb-uuid");
    this.doShare(uuid, this.current_asset_data.assetId);

    this.axl.sendMessage(AxL.ChildToHost.TRACK, {
      action: 'click share icon',
      category: 'websandbox_2gFc5DkygEURfw',
      label: `main - ${this.current_asset_data.assetId}`,
    });
  }

  randomMix() {
    this.doRandomMix();

    this.axl.sendMessage(AxL.ChildToHost.TRACK, {
      action: 'click random remix',
      category: 'websandbox_2gFc5DkygEURfw',
      label: `random - ${this.current_asset_data.assetId}`,
    });
  }

  remix() {
    this.loadRemixImage();

    this.axl.sendMessage(AxL.ChildToHost.TRACK, {
      action: 'click random remix',
      category: 'websandbox_2gFc5DkygEURfw',
      label: `remix button - ${this.current_asset_data.assetId}`,
    });
  }

  async getAssetData(assetId) {
    // call to server for asset data
    const existingData = this.assetsMap.get(assetId);
    if (existingData && existingData.length > 0) {
      return existingData;
    }

    const data = await this.fetchWithTimeout(Endpoints.GET_ASSET_DATA, {
      assetIds: [assetId],
    });

    return data.getAssetsResponse.assets[0];
  }

  async checkTextInput(text) {
    this.showCheckingText();

    document.getElementById('enter-text').blur();

    if (text.length == 0) {
      this.showTextInputError();
      return;
    }

    const data = await this.fetchWithTimeout(Endpoints.CHECK_TEXT_INPUT, {
      text,
    });

    this.hideCheckingText();
    if (data.getTextSafetyResponse.decision == "SAFE") {
      this.hidePromptView();
      this.clearUserTextInput();
      this.doChangePromptWithUserInput(this.currentPromptIndex, text);
    } else {
      this.showTextInputError();
    }
  }

  async getRandomAssets() {
    const isFirstCall = this.assetsMap.size === 0;

    const data = await this.fetchWithTimeout(Endpoints.GET_RANDOM_ASSETS, {
      isFirstCall,
    }, () => {
      if (isFirstCall) {
        this.assetsMap.set(defaultAsset.assetId, defaultAsset);
        this.initMainImage(defaultAsset.assetId);
      }
    });

    return data ? data.getRandomArtRemixAssetResponse.assets : [defaultAsset];
  }

  onResize() {
    this.responsiveState = window.getComputedStyle(document.body, '::after').content.replace(/"/ig, '');
    this.resizePagination();
    this.resizeScrollables();
  }

  resizeScrollables() {
    for (const scrollable of this.scrollables) {
      scrollable.resize();
    }
  }

  //------ set a new painting asset
  async setMainImage(assetId, uuid) {
    if (!uuid) {
      this.currentRemixUuid = null;
    }

    const assetData = this.assetsMap.get(assetId);
    if (!assetData) {
      return;
    }

    this.remixImage.reset();

    this.current_asset_data = assetData;

    // override any previous image requets
    this.lastRequestId = null;

    const prompt = this.current_asset_data.aiRemixPromptTemplate;
    const $descriptionText = document.getElementById('description-text');

    while ($descriptionText.hasChildNodes()) {
      $descriptionText.removeChild($descriptionText.firstChild);
    }

    if (uuid) {
      const remix = assetData.remixes.get(uuid);
      let index = 0;
      for (const span of remix.promptChildren) {
        let cln = span.cloneNode(true);

        if (index === 0) {
          const textWithoutInspiredBy = cln.innerText.replace('Inspired by ', '');
          cln.innerText = textWithoutInspiredBy.charAt(0).toUpperCase() + textWithoutInspiredBy.slice(1);
        }

        $descriptionText.appendChild(cln);
        index++;
      }
      this.setPageMainImageState("remixed");
    } else if (prompt.length > 0) {
      // set prompt
      let prompt_inserts = this.current_asset_data.aiRemixPromptGroups;
      let splitPrompt = prompt.split(/(\{}+)/);
      let count = 0;
      splitPrompt.forEach((item, i) => {
        let span = document.createElement("span");
        if (item == "{}") {
          span.innerText = prompt_inserts[count][0];
          span.classList.add("remix-selection");
          span.setAttribute("data-index", count);
          span.setAttribute("data-selection", 0);
          span.setAttribute("remix-word", "true")
          count++;
        } else {
          span.innerText = item;
        }
        $descriptionText.appendChild(span);
      });
      this.resizeScrollables();
    }

    for (const item of $descriptionText.querySelectorAll('.remix-selection')) {
      item.addEventListener(
        "click",
        function (event) {
          this.doOpenChangePrompt(event);

          this.axl.sendMessage(AxL.ChildToHost.TRACK, {
            action: 'select text',
            category: 'websandbox_2gFc5DkygEURfw',
            label: this.current_asset_data.assetId,
          });
        }.bind(this)
      );
    }

    // set title and creator
    this.updateMainImageMeta();

    // set the image
    const image = uuid ? assetData.remixes.get(uuid).thumbnail : assetData.imageUrl;
    this.setPaintingImage(image);
  }

  // load a new image into main view
  setPaintingImage(src) {
    return new Promise((resolve, reject) => {
      let img = document.getElementById("painting-img");
      img.setAttribute("data-src", src);
      img.setAttribute("data-no-crop", true);
      this.loadImages(document.getElementById("painting"));
    });
  }

  //------ set suggested artworks ("more artworks" page)
  async setMoreArtworks() {
    let parent = document.querySelector(
      "#more-artworks .assets-grid"
    );
    let inc = 0;

    parent.innerHTML = '';

    const mapSize = this.assetsMap.size;
    const startInc = mapSize - 9;

    for (let [assetId, assetData] of this.assetsMap) {
      if (Object.keys(assetData).length > 0 && inc >= startInc && inc <= mapSize) {
        let li = document.createElement("li");

        let imgDiv = document.createElement("div");
        const fifeResizedUrl = assetData.imageUrl.replace(
          "s1200",
          "h300-c-w300"
        );
        imgDiv.setAttribute("data-src", fifeResizedUrl);
        imgDiv.classList.add("img");
        li.appendChild(imgDiv);

        li.setAttribute("data-assetid", assetId);

        parent.appendChild(li);

        li.addEventListener("click", (e) => {
          this.showMoreArtwork(e.currentTarget.getAttribute("data-assetid"));
        });

        this.loadImages(li);

        if (parent.children.length === 1) {
          this.showMoreArtwork(assetId);
        }
      }
      inc++;
    }
  }

  // ICI
  showMoreArtwork(assetId) {
    const clickedItem = document.querySelector(
      `#more-artworks [data-assetid="${assetId}"`
    );
    const previousClickedItem = clickedItem.parentNode.querySelector(".active");
    if (previousClickedItem) {
      previousClickedItem.classList.remove("active");
    }
    clickedItem.classList.add("active");

    const data = this.assetsMap.get(assetId);
    document.getElementById("more-artworks-title").innerText = data.title;

    let authorAndPartner = "";

    if (data.creators.length > 0) authorAndPartner += data.creators[0];

    if (data.partnerName) {
      if (authorAndPartner.length > 0) authorAndPartner += ", ";
      authorAndPartner += data.partnerName;
    }
    document.getElementById("more-artworks-meta").innerText = authorAndPartner;

    let promptHtml = data.aiRemixPromptTemplate;

    for (const promptGroup of data.aiRemixPromptGroups) {
      promptHtml = promptHtml.replace("{}", `<b>${promptGroup[0]}</b>`);
    }

    document.getElementById("more-artworks-prompt").innerHTML = promptHtml;
  }

  //------ your creations
  showYourCreation(uuid, li) {
    const clickedItem = li;
    const previousClickedItem = document.querySelector(
      "#your-creations .pagination-target .active"
    );
    if (previousClickedItem) {
      previousClickedItem.classList.remove("active");
    }
    clickedItem.classList.add("active");

    let promptEl = document.getElementById("your-creations-prompt-" + uuid);
    let myPromptDiv = document.getElementById("your-creations-prompt-text");
    myPromptDiv.innerHTML = "";

    NodeList.prototype.forEach = Array.prototype.forEach;
    let children = promptEl.childNodes;
    children.forEach(function (item) {
      let cln = item.cloneNode(true);
      myPromptDiv.appendChild(cln);
    });

    let assetId = li.firstChild.getAttribute("data-thumb-assetId");
    let assetData = this.assetsMap.get(assetId);

    this.resizeScrollables();
  }

  editYourCreation(target) {
    let selected = target ? target.firstChild : document.querySelector(
      "#your-creations .pagination-target .active"
    ).firstChild;
    let assetId = selected.getAttribute("data-thumb-assetId");
    let uuid = selected.getAttribute("data-thumb-uuid");

    let downloadButtonId = isCultureStudio
      ? "mobile-main-image-download-button"
      : "main-image-download-button";
    const mainDownloadButton = document.getElementById(downloadButtonId);
    mainDownloadButton.setAttribute('data-assetId', assetId);
    mainDownloadButton.setAttribute('data-uuid', uuid);

    this.setMainImage(assetId, uuid);
    this.showPage("main-image");
  }

  // random remix of all parts of prompt
  async doRandomMix() {
    if (this.current_asset_data == null || !this.canSendArtRemixRequest) {
      return;
    }

    // find new random prompts
    let prompt_indexes = [];
    this.current_asset_data.aiRemixPromptGroups.forEach((item, i) => {
      const randomIndex = Math.floor(Math.random() * item.length);
      prompt_indexes.push(randomIndex);
    });

    let prompt_groups = this.current_asset_data.aiRemixPromptGroups;
    let prompts = document.querySelectorAll("#description .remix-selection");

    prompts.forEach((item, i) => {
      setTimeout(function () {
        item.innerText = prompt_groups[i][prompt_indexes[i]];
        item.setAttribute("data-selection", prompt_indexes[i]);
      }, i * 100);

      setTimeout(function () {
        item.classList.remove("hide");
      }, 500 + (i + 1) * 100);
    });

    // do remix after the prompts have been set
    setTimeout(() => {
      this.resizeScrollables();
      this.loadRemixImage();
    }, 200);
  }

  // when a new image has been received
  newImageReceived() {
    // store to artworks with prompt
    this.storeCurrentToMyArtworks();

    let el = document.getElementById("painting-img");
    let parent = el.parentNode;
    parent.classList.remove("hide");
  }

  // copy image to my creations page
  storeCurrentToMyArtworks() {
    let uuid = this.uuidv4();

    const currentAssetData = this.current_asset_data;
    if (!currentAssetData.remixes) {
      currentAssetData.remixes = new Map();
    }

    currentAssetData.remixes.set(uuid, {});
    this.currentRemixUuid = uuid;

    let originalTitle = currentAssetData.title;

    let myWorkDiv = document.createElement("li");

    let myPromptContDiv = document.createElement("div");
    myPromptContDiv.classList.add("my-artworks-prompt-data");

    let myPromptDiv = document.createElement("div");
    myPromptDiv.id = "your-creations-prompt-" + uuid;
    myPromptDiv.classList.add("your-creations-prompt");
    myPromptDiv.setAttribute("data-uuid", uuid);

    let promptEl = document.getElementById("description-text");
    NodeList.prototype.forEach = Array.prototype.forEach;
    let children = promptEl.childNodes;
    currentAssetData.remixes.get(uuid).promptChildren = [];
    children.forEach(function (item, index) {
      let cln = item.cloneNode(true);
      currentAssetData.remixes.get(uuid).promptChildren.push(cln);
      myPromptDiv.appendChild(cln);
      const innerText = cln.innerText;
      if (index === 0) {
        cln.innerText = 'Inspired by ' + innerText.charAt(0).toLowerCase() + innerText.slice(1);
      } else {
        cln.innerText = innerText;
      }
    });

    myPromptContDiv.appendChild(myPromptDiv);

    let myThumbDiv = document.createElement("div");
    myThumbDiv.classList.add("your-creations-thumb");
    myThumbDiv.setAttribute("data-thumb-uuid", uuid);
    myThumbDiv.setAttribute(
      "data-thumb-assetId",
      this.current_asset_data.assetId
    );

    myWorkDiv.addEventListener("click", (e) => {
      if (this.responsiveState === 'portrait') {
        this.editYourCreation(e.currentTarget);
      } else {
        this.showInYourCreations(e.currentTarget);
      }
    });

    const myThumbnail = document.createElement("img");
    const src = document
      .getElementById("painting-img")
      .style.backgroundImage.slice(4, -1)
      .replace(/"/g, "");
    myThumbnail.crossOrigin = "anonymous";
    myThumbnail.id = "thumb_" + uuid;
    myThumbnail.src = src;
    myThumbDiv.appendChild(myThumbnail);
    currentAssetData.remixes.get(uuid).thumbnail = src;

    let myButtonsDiv = document.createElement("div");
    myButtonsDiv.classList.add("your-creations-downl-nav");
    let myDownload = document.createElement("button");
    let myShare = document.createElement("button");
    myDownload.setAttribute("data-assetId", currentAssetData.assetId);
    myDownload.setAttribute("data-uuid", uuid);
    myShare.setAttribute("data-assetId", currentAssetData.assetId);
    myShare.setAttribute("data-uuid", uuid);
    myDownload.classList.add("material-icons");
    myShare.classList.add("material-icons");
    myDownload.innerText = "file_download";
    myShare.innerText = "share";
    myButtonsDiv.appendChild(myShare);
    myButtonsDiv.appendChild(myDownload);
    myThumbDiv.appendChild(myButtonsDiv);

    myWorkDiv.appendChild(myThumbDiv);
    myWorkDiv.appendChild(myPromptContDiv);

    let parent = document.querySelector("#your-creations .pagination-target");
    parent.insertBefore(myWorkDiv, parent.firstChild);
    this.showYourCreation(uuid, myWorkDiv);

    myDownload.addEventListener(
      "click",
      function (e) {
        e.stopPropagation();
        let assetId = e.currentTarget.getAttribute("data-assetId");
        let uuid = e.currentTarget.getAttribute("data-uuid");
        this.doDownloadShareCard(uuid, assetId);
      }.bind(this)
    );

    myShare.addEventListener(
      "click",
      function (e) {
        e.stopPropagation();
        let assetId = e.currentTarget.getAttribute("data-assetId");
        let uuid = e.currentTarget.getAttribute("data-uuid");
        this.doShare(uuid, assetId)
      }.bind(this)
    );

    // Update attributes of the main download button
    let downloadButtonId = isCultureStudio
      ? "mobile-main-image-download-button"
      : "main-image-download-button";
    const mainDownloadButton = document.getElementById(downloadButtonId);
    mainDownloadButton.setAttribute('data-assetId', currentAssetData.assetId);
    mainDownloadButton.setAttribute('data-uuid', uuid);

    //update the pagination
    this.resizePagination();
  }

  showInYourCreations(target) {
    const previousClickedItem = document.querySelector(
      "#your-creations .pagination-target .active"
    );
    if (previousClickedItem) {
      previousClickedItem.classList.remove("active");
    }
    target.classList.add("active");

    const uuid = target.querySelector('.your-creations-thumb').getAttribute('data-thumb-uuid');

    let promptEl = document.getElementById("your-creations-prompt-" + uuid);
    let myPromptDiv = document.getElementById("your-creations-prompt-text");
    myPromptDiv.innerHTML = "";

    NodeList.prototype.forEach = Array.prototype.forEach;
    let children = promptEl.childNodes;
    children.forEach(function (item) {
      let cln = item.cloneNode(true);
      myPromptDiv.appendChild(cln);
    });

    let assetId = target.querySelector('.your-creations-thumb').getAttribute("data-thumb-assetId");
  }

  // reset prompt and image to original
  async doResetAll() {
    this.hidePrompts();

    await sleep(500);

    this.setPaintingImage(this.current_asset_data.imageUrl);

    // reset prompt
    if (this.current_asset_data.aiRemixPromptTemplate.length > 0) {
      let prompt_groups = this.current_asset_data.aiRemixPromptGroups;
      let prompts = document.querySelectorAll("#description .remix-selection");
      prompts.forEach((item, i) => {
        item.innerText = prompt_groups[i][0];
        item.setAttribute("data-selection", 0);
        setTimeout(function () {
          item.classList.remove("hide");
        }, (i + 1) * 100);
      });
    }
  }

  doShare(uuid, assetId) {
    this.axl.sendMessage(AxL.ChildToHost.SET_URL_PARAMS, { assetId: assetId });
    const shareText = isCultureStudio
        ? 'Hey, I’m sharing my #CultureStudio creation with you! Check it out then create your own.'
        : 'Remix your favorite artwork with Generative AI on Google Arts & Culture';

    if (AxL.can["shareFiles"]) {
      const imgData = this.getShareImageCardData(uuid, assetId);

      this.axl.sendMessage(AxL.ChildToHost.SHARE, {
        title: shareText,
        text: shareText,
        files: [{ data: imgData, name: "artremix.png", type: "image/png" }],
      });
    } else {
      this.axl.sendMessage(AxL.ChildToHost.SHARE, {
        title: shareText,
        text: shareText
      });
    }
  }

  doSaveCreation(uuid, assetId) {
    const imgData = this.getShareImageCardData(uuid, assetId);
    let promptNodes = document.querySelectorAll("#description .remix-selection");
    let prompts = [];
    promptNodes.forEach((promptNode) => {
      prompts.push(promptNode.innerText);
    });

    this.sendStudioAction('sc', {
      'a': assetId,
      'm': imgData,
      't': prompts.join(', ')
    });
  }

  getShareImageCardData(uuid, assetId) {
    let img = document.getElementById("thumb_" + uuid);
    let promptEl = document.getElementById("your-creations-prompt-" + uuid);
    this.sharePostcardMaker.makePostcard(img, promptEl);

    let dt = this.sharePostcardMaker.canvas.toDataURL("image/png");
    return dt.split(",")[1];
  }

  doDownloadShareCard(uuid, assetId) {
    let img = document.getElementById("thumb_" + uuid);
    let promptEl = document.getElementById("your-creations-prompt-" + uuid);
    this.sharePostcardMaker.makePostcard(img, promptEl);

    let dt = this.sharePostcardMaker.canvas.toDataURL("image/png");

    let link = document.createElement("a");
    link.href = dt;
    link.download = "artremix.png";
    link.click();
  }

  //----- prompt changing by user
  doOpenChangePrompt(e) {
    // set change prompt
    let promptIndex = parseInt(e.target.getAttribute("data-index"));
    let promptSelection = e.target.getAttribute("data-selection");
    let prompt_inserts = this.current_asset_data.aiRemixPromptGroups;

    this.currentPromptIndex = promptIndex;
    this.currentPromptSelection = promptSelection;

    let orignal = document.getElementById("original-value");
    orignal.innerHTML = "";

    let promptEl = document.getElementById("description-text");
    for (const child of promptEl.children) {
      if (
        child.hasAttribute("data-index") &&
        child.getAttribute("data-index") == promptIndex
      ) {
        let cln = child.cloneNode(true);
        orignal.appendChild(cln);
      } else {
        // On desktop, the first part of the prompt is in display: block.
        // Retrieving the innerText removes the space at the end ONLY when
        // in display: block.
        // Trimming and adding spaces manually fixes it.
        orignal.innerHTML += ' ' + child.innerText.trim() + ' ';
        ;
      }
    }

    // TODO: this is created every time. to fix
    orignal.addEventListener(
      "click",
      function () {
        this.doChangePromptSelection();
      }.bind(this)
    );

    let prompt_suggestions = prompt_inserts[promptIndex];

    // clear past values
    let parent = document.getElementById("sugg-value");
    while (parent.hasChildNodes()) {
      parent.removeChild(parent.firstChild);
    }

    // set suggestions
    prompt_suggestions.forEach((item, i) => {
      if (i != promptSelection) {
        let sugg = document.createElement("div");
        sugg.classList.add("suggestions");
        sugg.setAttribute("data-selection", i);
        sugg.addEventListener(
          "click",
          function (e) {
            let selection = e.target.getAttribute("data-selection");
            this.currentPromptSelection = selection;
            this.doChangePromptSelection();

            this.axl.sendMessage(AxL.ChildToHost.TRACK, {
              action: 'refined result',
              category: 'websandbox_2gFc5DkygEURfw',
              label: `suggested option - ${this.current_asset_data.assetId}`,
            });
          }.bind(this)
        );
        sugg.innerText = item.length > 24 ? item.substring(0, 24) + '...' : item;
        parent.appendChild(sugg);
      }
    });

    // update ui to get styles for this index
    this.showPromptView(promptIndex);
  }

  // change a single part of the prompt using pre-selected prompts
  async doChangePromptSelection() {
    if (this.current_asset_data == null) {
      return;
    }

    let index = this.currentPromptIndex;
    let selection = this.currentPromptSelection;

    let prompts = document.querySelectorAll("#description .remix-selection");
    let promptSelection = prompts[index];

    let doesPromptHaveCustom = false;
    prompts.forEach((item, i) => {
      if (i != index && item.getAttribute("data-selection") == "custom") {
        doesPromptHaveCustom = true;
      }
    });

    if (selection == "custom") {
      this.doChangePromptWithUserInput(index, promptSelection.innerText);
      return;
    }

    if (promptSelection.getAttribute("data-selection") == selection) {
      return;
    }

    // change prompt
    promptSelection.innerText =
      this.current_asset_data.aiRemixPromptGroups[index][selection];
    promptSelection.setAttribute("data-selection", selection);

    this.loadRemixImage();

    this.hidePromptView();
  }

  // change prompt using custom user input
  async doChangePromptWithUserInput(index, userText) {
    if (this.current_asset_data == null) {
      return;
    }

    let prompts = document.querySelectorAll("#description .remix-selection");
    let promptSelection = prompts[index];

    // change prompt
    promptSelection.innerText = userText;
    promptSelection.setAttribute("data-selection", "custom");

    this.loadRemixImage();
  }

  //------ load a live image
  async loadRemixImage() {
    // Disallow sending multiple remix image requests:
    // If a request is already in flight, block the next one.
    if (!this.canSendArtRemixRequest) {
      return;
    }

    this.setPageMainImageState("remixing");
    this.remixImage.pixelateImage();

    let asset_id = this.current_asset_data.assetId;

    let uuid = this.uuidv4();
    this.lastRequestId = uuid;

    // Noise strenght is determined with this formula that was decided by PMs.
    // This means we'll never send the value set in the progressbar, 
    // but it's apparently fine.
    let noiseStrength = this.noiseStrength * 0.8 + 0.2;
    if (this.hasUrlParam("noise")) {
      noiseStrength = this.getUrlParam("noise");
    }

    const remixWordsArray = Array.from(document.querySelectorAll("#description-text > [remix-word='true']"))
    const remixWords = remixWordsArray.map(element => element.innerText);

    // Block sending other remixing request.
    this.canSendArtRemixRequest = false;

    let data = {};
    try {
      data = await this.fetchWithTimeout(Endpoints.GET_REMIX_IMAGE, {
        asset_id: asset_id,
        text_prompt_words: remixWords,
        noise_strength: noiseStrength,
      });
    } catch(e) {
      this.axl.sendMessage(AxL.ChildToHost.TRACK, {
        action: 'remixing failed',
        category: 'websandbox_2gFc5DkygEURfw',
        label: asset_id || 'unset',
      });
    } finally {
      // When data comes back or request failed, allow sending another remix request.
      this.canSendArtRemixRequest = true;
    }

    if (
      data.hasOwnProperty("remixImageResponse") &&
      this.lastRequestId == uuid
    ) {
      const b64 =
        "data:image/png;base64," + data.remixImageResponse.imageBytes;

      this.setPageMainImageState("remixed");

      this.remixImage.updateSrc(b64);
      this.remixImage.unpixelateImage(b64);

      this.newImageReceived();
    } else {
      this.abortReason = AbortStates.REMIX;
      this.doLoadRemixError();
    }
  }

  cancelRemix() {
    this.abortReason = AbortStates.CANCEL;
    this.loadRemixAbortController.abort();
  }

  doLoadRemixError() {
    this.setPageMainImageState(this.currentRemixUuid ? 'remixed' : 'original');

    if (this.abortReason !== AbortStates.CANCEL) {
      const promptContainer = document.getElementById("prompt-error-container");
      promptContainer.classList.remove('hide');
      promptContainer.setAttribute('data-state', this.abortReason);

      setTimeout(() => {
        promptContainer.classList.add('hide');
      }, 10000);
    }

    if (this.current_asset_data.aiRemixPromptTemplate.length > 0) {
      let promptGroups = this.current_asset_data.aiRemixPromptGroups;

      if (this.currentRemixUuid) {
        const promptChildren = this.current_asset_data.remixes.get(this.currentRemixUuid).promptChildren;
        promptGroups = [];

        for (const promptItem of promptChildren) {
          if (promptItem.classList.contains('remix-selection')) {
            promptGroups.push([promptItem.innerText]);
          }
        }
      }

      let prompts = document.querySelectorAll("#description .remix-selection");
      prompts.forEach((item, i) => {
        item.innerText = promptGroups[i][0];
        item.setAttribute("data-selection", 0);
        setTimeout(function () {
          item.classList.remove("hide");
        }, (i + 1) * 100);
      });
    }

    // reshow navigation for image
    this.remixImage.unpixelateImage();
    this.abortReason = null;
  }

  //----- utils
  uuidv4() {
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
      (
        c ^
        (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
      ).toString(16)
    );
  }

  //------ ui behaviors
  showCheckingText() {
    document.getElementById("enter-text-loading").classList.add("show");
  }

  hideCheckingText() {
    document.getElementById("enter-text-loading").classList.remove("show");
  }

  showTextInputError() {
    document.getElementById("enter-text-error").classList.add("show");
    setTimeout(() => {
      document.getElementById("enter-text-error").classList.remove("show");
    }, 2000);
  }

  hidePrompts() {
    let prompts = document.querySelectorAll("#description .remix-selection");
    prompts.forEach((item, i) => {
      setTimeout(function () {
        item.classList.add("hide");
      }, i * 100);
    });
  }

  showPage(id, isBack = false) {
    let previousPageId = this.history[this.history.length - 1];

    if (id === previousPageId && !isBack) {
      return;
    }

    const newPage = document.getElementById(id);

    if (!newPage) {
      return;
    }

    if (isBack) {
      this.history.pop();
    } else {
      this.history.push(id);
    }

    newPage.classList.add("show");

    switch (id) {
      case "more-artworks":
        this.setMoreArtworks();
        break;
      case "your-creations":
        this.resizePagination();
        break;
      default:
        break;
    }

    const previousPage = document.getElementById(previousPageId);

    if (!previousPage) {
      return;
    }

    previousPage.classList.remove("show");
  }

  async initMainImage(assetId) {
    const asset = this.assetsMap.get(assetId);
    if (asset) {
      this.setMainImage(assetId);
    }
  }

  showPromptView(index) {
    const promptChangeContainer = document.getElementById(
      "prompt-change-container"
    );
    promptChangeContainer.setAttribute("data-index", index);
    promptChangeContainer.classList.add("show");

    document.getElementById("description-inner").classList.add("hide");
  }

  hidePromptView() {
    document.getElementById("prompt-change-container").classList.remove("show");
    document.getElementById("description-inner").classList.remove("hide");
  }

  clearUserTextInput() {
    document.querySelector("#enter-text").innerText = '';
  }

  openLandscapeDialog(id) {
    // hide prompt description and navigation
    document.getElementById("description").classList.add("hide");
    document.getElementById("remix-nav").classList.add("hide");
  }

  onPaginationButtonClick(e) {
    const parent = e.currentTarget.parentNode;
    const pages = parent.querySelectorAll("li");
    const currentPage = e.currentTarget.parentNode.querySelector("li.active");
    const currentPageIndex = parseInt(currentPage.innerText);
    const lastPageIndex = parseInt(pages[pages.length - 1].innerText);
    const isPrev = e.currentTarget.classList.contains("pagination-button-prev");

    if (
      (isPrev && currentPageIndex === 1) ||
      (!isPrev && currentPageIndex === lastPageIndex)
    ) {
      return;
    }

    const newIndex = currentPageIndex + (isPrev ? -1 : 1);
    currentPage.classList.remove("active");
    pages[newIndex - 1].classList.add("active");

    const buttonPrev = parent.querySelector(".pagination-button-prev");
    const buttonNext = parent.querySelector(".pagination-button-next");

    if (newIndex > 1 && buttonPrev.classList.contains("disabled")) {
      buttonPrev.classList.remove("disabled");
    } else if (newIndex === 1 && !buttonPrev.classList.contains("disabled")) {
      buttonPrev.classList.add("disabled");
    }

    if (newIndex < lastPageIndex && buttonNext.classList.contains("disabled")) {
      buttonNext.classList.remove("disabled");
    } else if (
      newIndex === lastPageIndex &&
      !buttonNext.classList.contains("disabled")
    ) {
      buttonNext.classList.add("disabled");
    }

    const target = parent.parentNode.querySelector(".pagination-target");
    target.setAttribute("data-page", newIndex - 1);

    this.loadImages(target);
  }

  resizePagination() {
    if (!this.history[this.history.length - 1] === 'your-creations') {
      return;
    }

    const $container = document.querySelector('.pagination-container');
    const $items = $container.querySelectorAll('.pagination-target li');
    const itemsCount = $items.length;
    const itemsPerPage = this.responsiveState === 'portrait' ? 3 : 9;
    const pagesNumber = Math.ceil(itemsCount / itemsPerPage);

    $container.setAttribute('data-pages', pagesNumber);

    if (itemsCount > 0) {
      document.querySelector("#your-creations-button .count").innerText = itemsCount > 9 ? '9+' : itemsCount;
    }

    if (pagesNumber === 1) {
      return;
    }

    const $paginationPagesContainer = $container.querySelector('.pagination-pages');
    $paginationPagesContainer.innerHTML = '';

    for (let i = 0; i < pagesNumber; i++) {
      const $el = document.createElement('li');

      $el.innerText = i + 1;

      if (i === 0) {
        $el.classList.add('active');
      }

      $paginationPagesContainer.appendChild($el);
    }

    $container.setAttribute('data-page', 0);
  }

  updateMainImageMeta() {
    let title = this.current_asset_data.title;
    let partner = this.current_asset_data.partnerName;
    let creator =
      this.current_asset_data.creators.length > 0
        ? this.current_asset_data.creators[0]
        : "";
    let assetId = this.current_asset_data.assetId;

    document.getElementById("painting-meta-partner").innerText = partner;
    document.getElementById("painting-meta-author").innerText = creator;
    document.getElementById("painting-meta-title").innerText = title;

    document.getElementById("painting-meta").setAttribute('data-href', `asset/${assetId}`);
    document.getElementById("painting-meta").addEventListener('click', (e) => {
      this.axl.sendMessage(AxL.ChildToHost.NAVIGATE, e.currentTarget.getAttribute('data-href'));
    });
  }

  setPageMainImageState(subPage) {
    if (subPage === 'main-image') {
      this.currentRemixUuid = null;
    }

    document.getElementById("main-image").setAttribute("data-state", subPage);
  }

  loadImages(container) {
    const imgList = container.querySelectorAll("[data-src]");

    for (const img of imgList) {
      const src = img.getAttribute("data-src");

      if (src) {
        const bounds = img.getBoundingClientRect();
        const height = parseInt(bounds.height);
        const width = parseInt(bounds.width);
        let fifeResizedUrl = src.replace("s1200", `h${height}-w${width}`);

        if (!img.getAttribute("data-no-crop")) {
          fifeResizedUrl += "-c";
        }

        if (height !== 0 && width !== 0) {
          const imgToLoad = new Image();
          imgToLoad.onload = () => {
            img.style.backgroundImage = `url('${fifeResizedUrl}')`;
            img.classList.add("loaded");
            img.setAttribute("data-src", "");
          };
          imgToLoad.src = fifeResizedUrl;
          imgToLoad.crossOrigin = "anonymous";
        }
      }
    }
  }

  async fetchWithTimeout(endpoint, params, errorCallback) {
    const timeout = endpoint === Endpoints.GET_REMIX_IMAGE ?
      API_REMIX_TIMEOUT_MS :
      API_REQUEST_TIMEOUT_MS;
    const errorState = endpoint === Endpoints.GET_REMIX_IMAGE ?
      AbortStates.REMIX :
      AbortStates.DEFAULT;
    this.loadRemixAbortController = new AbortController();
    const shouldNotShowError =
      this.assetsMap.size === 0 &&
      endpoint === Endpoints.GET_RANDOM_ASSETS;

    this.modelTimeoutTimer = setTimeout(() => {
      this.abortReason = errorState;
      this.loadRemixAbortController.abort();
    }, timeout);

    const url = `${window.location.origin +
      (window.location.pathname === "/" ? "" : window.location.pathname)
      }/${endpoint}`;

    const response = await fetch(url, {
      method: "POST",
      signal: this.loadRemixAbortController.signal,
      body: JSON.stringify(params),
      headers: { "Content-Type": "application/json" },
    }).catch(() => {
      // Things are handled right after.
    });

    clearTimeout(this.modelTimeoutTimer);

    if (endpoint === Endpoints.GET_RANDOM_ASSETS && this.history[this.history.length - 1] === 'more-artworks') {
      document.getElementById('more-artworks-button-refresh').classList.remove('loading');
    }

    if (!response || !response.ok) {
      if (errorCallback) {
        errorCallback();
      }

      if (shouldNotShowError) {
        return;
      }

      this.abortReason = this.abortReason || errorState;
      this.doLoadRemixError();
      const message = response && response.status || 'There was a problem fetching the data.';
      throw new Error(message);
    }

    return await response.json();
  }

  sendStudioAction(command, params = {}) {
    params['c'] = command;
    this.axl.sendMessage(AxL.ChildToHost.STUDIO_ACTION, params);
  }

  async onSwitchArtworkFromHost(assetId) {
    this.assetsMap = new Map();
    await this.setAssets(assetId);
    this.setMainImage(assetId);
    this.setPageMainImageState("original");
  }
}
